import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { CountryCombobox } from "@/components/ui/customs/country-combobox";
import { FilterV2, AdditionalFilterV2, useFilterV2 } from "@/components/shared/filter-v2";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { useGetCustoms } from "@/api/customs.api";
import { useGetUsersBo } from "@/api/user.api";
import { CustomsCombobox } from "@/components/ui/customs/customs-combobox";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { DateRangePicker } from "@/components/ui/customs/date-range-picker";
import { COUNTRY_REGIONS, REGION_LABELS, type RegionCode } from "@/constants/countries";
import { RolesEnum } from "@/constants/enums/roles.enum";
import type {
  ValidatedExpeditionsFilters as FiltersState,
  useValidatedExpeditionsFilters,
} from "./useValidatedExpeditionsFilters.reducer";
import { useManualTrackingOptions, usePackagingFilterOptions } from "@/hooks/useEnumOptions";

const regionOptions = (Object.keys(COUNTRY_REGIONS) as RegionCode[]).map((code) => ({
  label: REGION_LABELS[code],
  value: code,
}));

const FINALIZED_BY_ROLES = [RolesEnum.ADMIN, RolesEnum.MEMBER];

type SharedProps = {
  filters: FiltersState;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
};

const FinalizedByFilter = ({ filters, actions }: SharedProps) => {
  const { t } = useTranslation("boExpeditions");
  const { data: boUsers } = useGetUsersBo({ role: FINALIZED_BY_ROLES, isDeleted: false });
  const options = useMemo(
    () =>
      (boUsers ?? []).map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      })),
    [boUsers],
  );

  return (
    <div className="max-w-xs min-w-[200px] flex-1">
      <SelectV2
        placeholder={t("deletedFilters.finalizedByPlaceholder")}
        clearable
        options={options}
        onChange={(e) => actions.setFinalizedById(e || null)}
        value={filters.finalizedById ?? undefined}
      />
    </div>
  );
};

type DeletedExpeditionsFiltersProps = SharedProps;

export const DeletedExpeditionsFilters = ({ filters, actions }: DeletedExpeditionsFiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const manualTrackingOptions = useManualTrackingOptions();
  const { isOpen, toggle } = useFilterV2();
  const { data: customs } = useGetCustoms();

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-col items-start justify-between gap-2 xl:flex-row">
        <div className="flex w-72 flex-row items-center gap-2">
          <UnifiedInput
            inputType="text"
            leftElement={<Search />}
            value={filters.search}
            onChange={(e) => actions.setSearch(String(e || ""))}
            className="placeholder:text-[#94A3B8]"
            placeholder={t("deletedFilters.searchPlaceholder")}
          />
        </div>
        <FilterV2
          isOpen={isOpen}
          onToggle={toggle}
          onReset={actions.resetAdvancedFilters}
          hasAdditionalFilters
          className="w-fit self-end"
        >
          <div className="flex w-fit flex-wrap items-center gap-2">
            <div className="w-44">
              <SelectV2
                placeholder={t("deletedFilters.regionPlaceholder")}
                clearable
                options={regionOptions}
                onChange={(e) => actions.setRegion((e as RegionCode) ?? null)}
                value={filters.region ?? undefined}
                searchable={false}
              />
            </div>
            <div className="max-w-xs min-w-[200px] flex-1">
              <SelectV2
                placeholder={t("deletedFilters.trackingPlaceholder")}
                clearable
                options={manualTrackingOptions}
                onChange={(e) => actions.setManualTracking(e)}
                value={filters.manualTracking ?? undefined}
              />
            </div>
            <div className="w-60">
              <CustomsCombobox
                customs={customs ?? []}
                value={filters.customId ?? undefined}
                onValueChange={actions.setCustomId}
                clearable
              />
            </div>
          </div>
        </FilterV2>
      </div>
      <AdditionalFilterV2 isOpen={isOpen}>
        <DeletedExpeditionsSecondaryContent filters={filters} actions={actions} />
      </AdditionalFilterV2>
    </div>
  );
};

const DeletedExpeditionsSecondaryContent = ({ filters, actions }: SharedProps) => {
  const { t } = useTranslation("boExpeditions");
  const packagingOptions = usePackagingFilterOptions();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="max-w-xs min-w-[200px] flex-1">
        <CountryCombobox
          value={filters.country ?? undefined}
          onValueChange={actions.setCountry}
          placeholder={t("deletedFilters.countryPlaceholder")}
          clearable
          allowedCodes={filters.region ? COUNTRY_REGIONS[filters.region] : undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <SelectV2
          placeholder={t("deletedFilters.packagingPlaceholder")}
          clearable
          options={packagingOptions}
          onChange={(e) => actions.setPackagingType((e as PackagingFilterEnum) ?? null)}
          searchable={false}
          value={filters.packagingType ?? undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <DateRangePicker
          placeholder={t("deletedFilters.deletedAtPlaceholder")}
          value={filters.deletedAt}
          onChange={actions.setDeletedAtRange}
        />
      </div>
      <FinalizedByFilter filters={filters} actions={actions} />
    </div>
  );
};
